import { useMemo } from 'react';
import { ProfileGalleryImage } from '../../../../api/Providers/GalleryImages';
import { ImageRow } from './types';

type ImageGridResult = {
  rows: Array<ImageRow>;
  hasMore: boolean;
};

export function useImageGrid(
  maxDesiredRows: number,
  rowSize: number,
  isEditMode: boolean,
  images: Array<ProfileGalleryImage>,
): ImageGridResult {
  /** The layout of images to display in the grid */
  const rows: Array<ImageRow> = useMemo<Array<ImageRow>>(() => {
    const result: Array<ImageRow> = [];
    const imageCount = images.length;
    const maxEditImages = Math.max(1, maxDesiredRows * rowSize - imageCount);

    // for each row we wish to display
    for (let rowIdx: number = 0; rowIdx < maxDesiredRows; rowIdx++) {
      // create the row object
      const row: ImageRow = {
        images: [],
        key: `row-${rowIdx}`,
      };

      // for each image we wish to display
      for (let column: number = 0; column < rowSize; column++) {
        const overallIndex = rowIdx * rowSize + column;

        if (isEditMode) {
          /*
            Pro-side algorithm is as follows:

            - if there is 1 image, show a single row:
              [empty, empty, img]
            - if there are 2 images, show a single row:
              [empty, img, img]
            - if there are 3 images, show two rows:
              [empty, empty, empty]
              [img, img, img]
            - if there are 4 images, show two rows:
              [empty, empty, img]
              [img, img img]
            - if there are 5 or more images, show two rows:
              [empty, img, img]
              [img, img, img]
            */

          // we want to put our empty images at the beginning of the list, and make sure we have
          // enough to fill all of the slots. we also always want at least one empty
          if (overallIndex < maxEditImages) {
            row.images.push({ empty: true, id: -overallIndex - 1 });
          } else {
            row.images.push(images[overallIndex - maxEditImages]);
          }
          // if not in edit mode, just add each image until we run out, then pad with empty images
        } else if (images.length > overallIndex) {
          // if we haven't run out
          row.images.push(images[overallIndex]);
        } else {
          // if we have run out
          row.images.push({ empty: true, id: -overallIndex - 1 });
        }
      }

      // add the row object to the list
      result.push(row);
    }

    return result;
  }, [
    images,
    isEditMode,
    maxDesiredRows,
    rowSize,
  ]);

  /** True if there are more images than will fit in the gallery */
  const hasMore: boolean = useMemo<boolean>(
    () => (images.length > (
      // only show one row in mobile
      maxDesiredRows * rowSize
      // edit mode always has at least one "empty" image
      - (isEditMode ? 1 : 0)
    )),
    [
      isEditMode,
      images.length,
      maxDesiredRows,
      rowSize,
    ],
  );

  return { rows, hasMore };
}
